import { useEffect, useState } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { useAccessToken } from '../auth/use-access-token.js';
import { apiFetch, ApiError, NotSignedIn } from './client.js';

/**
 * One hour, by its id.
 *
 * FOUR STATES, NOT THREE. `gone` is separate from `failed` on purpose: the
 * screen before this had only "found or not", so a 422 from a refused window
 * rendered as "that hour has gone" — a statement about the world made on the
 * strength of a failed request. An hour that is genuinely unbookable is a 404
 * and nothing else is.
 *
 * The screen used to ask `/api/slots` for a 183-day window and pick one row
 * out of the answer. That route caps a window at 31 days, so it was refused
 * every time and nobody could book anything.
 */

export interface SlotDetail {
  slot_id: string;
  slot_start: string;
  slot_end: string;
  /** Null while the hour is unclaimed and could still become any product. */
  claimed_tier_id: string | null;
  /** Null on an unclaimed hour: its size is not decided until a product claims it. */
  seats_free: number | null;
  coach_id: string;
  coach_name: string;
  facility_id: string;
  facility_name: string;
  resource_id: string;
  resource_name: string;
  /** For Assessments only (032; board B3). */
  for_assessments: boolean;
  /** The teams a reserved hour is for; empty = open to anyone. */
  reserved_for: string[];
  /** On a reserved hour, the family's members on its teams: the only ones it can take. */
  team_member_ids: string[];
  /** Who already has an Assessment: left out when Assessment is being booked (one per athlete). */
  assessed_ids: string[];
}

export type SlotState =
  | { status: 'loading' }
  | { status: 'ready'; slot: SlotDetail }
  | { status: 'gone' }
  | { status: 'failed'; message: string };

export function useSlot(
  slotId: string | undefined,
  excludeCartId?: string | null,
  /** The MPC coach's athlete from Book, so the hour says whether they have an Assessment. */
  athleteClientId?: string | null,
): { state: SlotState; reload: () => void } {
  const isAuthenticated = useIsAuthenticated();
  const getToken = useAccessToken();
  const [state, setState] = useState<SlotState>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!slotId) {
      setState({ status: 'gone' });
      return;
    }

    let cancelled = false;
    setState({ status: 'loading' });

    const params = new URLSearchParams();
    if (excludeCartId) params.set('exclude_cart_id', excludeCartId);
    if (athleteClientId) params.set('athlete_client_id', athleteClientId);
    const query = params.size > 0 ? `?${params.toString()}` : '';

    apiFetch<SlotDetail>(`/api/slots/${slotId}${query}`, getToken)
      .then((slot) => {
        if (!cancelled) setState({ status: 'ready', slot });
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        if (cause instanceof NotSignedIn) return;

        // The one answer that means the hour itself is unavailable. Everything
        // else is this app failing, and must not be reported as a fact about
        // the hour.
        if (cause instanceof ApiError && cause.status === 404) {
          setState({ status: 'gone' });
          return;
        }

        setState({
          status: 'failed',
          message:
            cause instanceof ApiError
              ? cause.message
              : 'The API did not answer. Is the Functions host running on port 7071?',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, getToken, slotId, excludeCartId, athleteClientId, nonce]);

  return { state, reload: () => setNonce((n) => n + 1) };
}
